import { useEffect, useRef, useState } from "react";
import { FormattedMessage } from "react-intl";

// Components
import {
    Box,
    Button,
    Typography,
    IconButton,
    Popper,
    Switch,
    Alert,
    AlertTitle,
    ClickAwayListener,
    Paper,
    Skeleton,
} from "@mui/material";
import { TagName } from "components/TagName";

// Services
import { updateUserOptinsService } from "services";

// Store
import { setToastConfig } from "store/slice/UI";

// Utils
import { useAppziWidget } from "utils/hooks";

// icons
import InfoIcon from "@mui/icons-material/Info";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";

// styles
import privacySettingsStyles from "./PrivacySettings.styles";

// images
import privacyEqualizer from "assets/images/pngs/privacy-equalizer.png";
import { useAppDispatch, useAppSelector } from "app/hooks";
import { getUserOrgs } from "store/slice/sysadmin/userOrgs/userOrgsSlice";

// types
import { Hub } from "../AccountManager";

const PrivacySettings = ({
    dataPrivacyChecked,
    setDataPrivacyChecked,
    hubAccess,
    setHubAccess,
    isLoading,
}: {
    dataPrivacyChecked: boolean;
    setDataPrivacyChecked: React.Dispatch<React.SetStateAction<boolean>>;
    hubAccess: Hub[];
    setHubAccess: React.Dispatch<React.SetStateAction<Hub[]>>;
    isLoading: boolean;
}) => {
    const classes = privacySettingsStyles();

    const { handleOpenWidget } = useAppziWidget();
    const dispatch = useAppDispatch();

    const userOrganizations = useAppSelector(getUserOrgs);

    const [showWarning, setShowWarning] = useState(false);
    const [anchorEl, setAnchorEl] = useState<{ element: null | HTMLElement; id: string }>({ element: null, id: "" });
    const [shareWithAll, setShareWithAll] = useState<boolean>(false);
    const [showScrollButton, setShowScrollButton] = useState<boolean>(false);
    const dataPrivacyRef = useRef<HTMLDivElement>(null);

    const open = Boolean(anchorEl.element);

    const handleUpdateDataPrivacy = async (newVal: boolean) => {
        try {
            await updateUserOptinsService({ share_data: newVal });
            setDataPrivacyChecked(newVal);
        } catch (error) {
            setDataPrivacyChecked(!newVal);
            console.error(error);
        } finally {
            setShowWarning(false);
        }
    };

    const handleClick = (event: React.MouseEvent<HTMLElement>, id: string) => {
        setAnchorEl({ element: event.currentTarget, id });
    };

    const handleSwitchChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
        if (!event.target.checked) {
            setShowWarning(true);
        } else {
            handleUpdateDataPrivacy(event.target.checked);
        }
    };

    const handleHubSwitchChange = (event: React.ChangeEvent<HTMLInputElement>, index: number) => {
        const updatedHubAccess = [...hubAccess];
        updatedHubAccess[index].sharing = event.target.checked;

        setHubAccess(updatedHubAccess);

        if (!updatedHubAccess[index].sharing) {
            dispatch(
                setToastConfig({
                    title: "Privacy settings changed.",
                    content: `You are no longer sharing your PACE profile with ${updatedHubAccess[index].name}.`,
                })
            );
        }

        const allHubsSharing = updatedHubAccess.every((hub) => hub.sharing);
        setShareWithAll(allHubsSharing);
    };

    const handleGlobalSwitchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const newSharingState = event.target.checked;
        setShareWithAll(newSharingState);

        const updatedHubAccess = hubAccess.map((hub) => ({
            ...hub,
            sharing: newSharingState,
        }));

        setHubAccess(updatedHubAccess);
    };

    useEffect(() => {
        const currentRef = dataPrivacyRef.current;
        const observer = new IntersectionObserver(
            (entries) => {
                const entry = entries[0];
                setShowScrollButton(!entry.isIntersecting);
            },
            { threshold: 1.0 }
        );

        if (currentRef) {
            observer.observe(currentRef);
        }

        return () => {
            if (currentRef) {
                observer.unobserve(currentRef);
            }
        };
    }, [dataPrivacyRef]);

    useEffect(() => {
        if (dataPrivacyRef.current) {
            const rect = dataPrivacyRef.current.getBoundingClientRect();
            if (rect.top >= 0 && rect.bottom <= window.innerHeight) {
                setShowScrollButton(false);
            }
        }
    }, []);

    const scrollToDataPrivacy = () => {
        dataPrivacyRef.current?.scrollIntoView({ behavior: "smooth" });
    };

    return (
        <Box className={classes.main}>
            <Box className={classes.content}>
                {isLoading ? (
                    <>
                        <Box className={classes.row}>
                            <Skeleton animation="wave" sx={{ fontSize: 42 }} />
                            <Skeleton variant="rectangular" animation="wave" height={64} />
                        </Box>
                        <Box className={classes.row}>
                            <Skeleton animation="wave" sx={{ fontSize: 42 }} />
                            <Skeleton variant="rectangular" animation="wave" height={64} />
                        </Box>
                    </>
                ) : (
                    <>
                        {!dataPrivacyChecked && !showWarning ? (
                            <Box className={classes.textDisabledDataPrivacy}>
                                <WarningAmberRoundedIcon className="icon" />

                                <Typography variant="subtitle2">
                                    <FormattedMessage id="accountManager.privacySettings.dataPrivacy.alert" />
                                </Typography>
                                {showScrollButton && (
                                    <Button
                                        variant="outlined"
                                        className={classes.scrollButton}
                                        onClick={scrollToDataPrivacy}
                                    >
                                        Share my PACE
                                    </Button>
                                )}
                            </Box>
                        ) : null}
                        {userOrganizations.data.length ? (
                            <Box className={classes.row}>
                                <Box display="flex">
                                    <Typography variant="h6" className={classes.rowTitle}>
                                        <FormattedMessage id="accountManager.privacySettings.hubAccessTitle" />
                                    </Typography>
                                    <IconButton
                                        onClick={(ev) => handleClick(ev, "hubAccess")}
                                        disableRipple={true}
                                        disableFocusRipple={true}
                                    >
                                        <InfoIcon className={classes.infoIcon} />
                                    </IconButton>
                                    <ClickAwayListener
                                        mouseEvent="onMouseDown"
                                        touchEvent="onTouchStart"
                                        onClickAway={() => setAnchorEl({ element: null, id: "" })}
                                    >
                                        <Popper
                                            id="hub-popper"
                                            open={open && anchorEl.id === "hubAccess"}
                                            anchorEl={anchorEl.element}
                                            placement="bottom-start"
                                        >
                                            <Paper
                                                className={classes.paper}
                                                sx={{ width: "569px !important" }}
                                                elevation={1}
                                            >
                                                <Box display="flex" alignItems="center" mb={1}>
                                                    <InfoIcon
                                                        className={classes.infoIcon}
                                                        sx={{ fontSize: "20px !important" }}
                                                    />
                                                    <Typography variant="h6" className={classes.tooltipTitle}>
                                                        <FormattedMessage id="accountManager.privacySettings.hubAccessTooltip.title" />
                                                    </Typography>
                                                </Box>
                                                <Typography sx={{ lineHeight: "24px", marginBottom: 2 }}>
                                                    <FormattedMessage id="accountManager.privacySettings.hubAccessTooltip.body" />
                                                </Typography>
                                            </Paper>
                                        </Popper>
                                    </ClickAwayListener>
                                </Box>

                                <Box sx={{ justifyContent: "flex-start" }} className={classes.rowDetails}>
                                    <Typography variant="body1" className={classes.hubAccessDescription}>
                                        <FormattedMessage id="accountManager.privacySettings.hubAccessText" />
                                    </Typography>
                                </Box>
                                {userOrganizations.data.length > 1 ? (
                                    <Box sx={{ display: "flex" }}>
                                        <Switch
                                            size="small"
                                            checked={shareWithAll}
                                            onChange={handleGlobalSwitchChange}
                                            className={classes.switch}
                                        />
                                        <Typography variant="body1">Sharing with All Hubs</Typography>
                                    </Box>
                                ) : null}

                                <Box>
                                    {userOrganizations.data.map((hub, index) => (
                                        <Box key={hub.id} className={classes.hubContainer}>
                                            <TagName>{hub.name}</TagName>
                                            <Box display="flex">
                                                <Typography variant="body1">Not Sharing</Typography>
                                                <Switch
                                                    size="small"
                                                    checked={hubAccess[index].sharing}
                                                    onChange={(event) => handleHubSwitchChange(event, index)}
                                                    className={classes.switch}
                                                />
                                                <Typography variant="body1">Sharing</Typography>
                                            </Box>
                                        </Box>
                                    ))}
                                </Box>
                            </Box>
                        ) : null}

                        {/* Data Privacy Section */}
                        <Box className={classes.row}>
                            <Box display="flex" ref={dataPrivacyRef}>
                                <Typography variant="h6" className={classes.rowTitle}>
                                    <FormattedMessage id="accountManager.privacySettings.dataPrivacyTitle" />
                                </Typography>

                                <IconButton
                                    onClick={(ev) => handleClick(ev, "dataPrivacy")}
                                    disableRipple={true}
                                    disableFocusRipple={true}
                                >
                                    <InfoIcon className={classes.infoIcon} />
                                </IconButton>
                                <ClickAwayListener
                                    mouseEvent="onMouseDown"
                                    touchEvent="onTouchStart"
                                    onClickAway={() => setAnchorEl({ element: null, id: "" })}
                                >
                                    <Popper
                                        id="data-privacy-popper"
                                        open={open && anchorEl.id === "dataPrivacy"}
                                        anchorEl={anchorEl.element}
                                        placement="bottom-start"
                                    >
                                        <Paper className={classes.paper} elevation={1}>
                                            <Typography>
                                                <FormattedMessage id="accountManager.privacySettings.popper.text1" />
                                            </Typography>
                                            <img src={privacyEqualizer} alt="privacyEqualizer" />
                                            <Typography sx={{ mb: 2 }}>
                                                <FormattedMessage id="accountManager.privacySettings.popper.text2" />
                                            </Typography>
                                            <Typography>
                                                <FormattedMessage id="accountManager.privacySettings.popper.text3" />
                                            </Typography>
                                        </Paper>
                                    </Popper>
                                </ClickAwayListener>
                            </Box>

                            <Box sx={{ justifyContent: "flex-start" }} className={classes.rowDetails}>
                                <Typography variant="body1" sx={{ marginTop: 1, marginBottom: 2 }}>
                                    <FormattedMessage id="accountManager.privacySettings.dataPrivacyText" />
                                </Typography>

                                <Switch
                                    size="small"
                                    sx={{ marginLeft: "16px" }}
                                    checked={dataPrivacyChecked}
                                    onChange={(event) => handleSwitchChange(event)}
                                    className={classes.switch}
                                />
                            </Box>
                            {showWarning ? (
                                <Alert
                                    severity="error"
                                    variant="filled"
                                    icon={false}
                                    className={classes.alert}
                                    sx={{ padding: 1 }}
                                >
                                    <AlertTitle>
                                        <Box sx={{ display: "flex", alignItems: "center", marginBottom: 2 }}>
                                            <ErrorOutlineIcon sx={{ fontSize: 18, marginRight: 1 }} />
                                            <Typography variant="subtitle1">
                                                <FormattedMessage id="accountManager.privacySettings.dataPrivacy.alert" />
                                            </Typography>
                                        </Box>
                                    </AlertTitle>

                                    <Box className={classes.warningMessageButtonsContainer}>
                                        <Button
                                            size="small"
                                            variant="outlined"
                                            className="go-back"
                                            onClick={() => setShowWarning(false)}
                                        >
                                            <FormattedMessage id="accountManager.privacySettings.goBack" />
                                        </Button>
                                        <Button
                                            size="small"
                                            variant="outlined"
                                            className="accept"
                                            onClick={() => handleUpdateDataPrivacy(false)}
                                        >
                                            <FormattedMessage id="accountManager.privacySettings.yesIAmSure" />
                                        </Button>
                                    </Box>
                                </Alert>
                            ) : null}
                        </Box>
                    </>
                )}
            </Box>
            {!isLoading && (
                <Box display="flex" alignItems="center">
                    <Typography variant="subtitle2">
                        <FormattedMessage id="accountManager.privacySettings.footer" />
                    </Typography>
                    <Button variant="text" size="small" className={classes.footerButton} onClick={handleOpenWidget}>
                        <Typography variant="subtitle2">
                            <FormattedMessage id="accountManager.privacySettings.footerButton" />
                        </Typography>
                    </Button>
                </Box>
            )}
        </Box>
    );
};

export default PrivacySettings;
